import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { naira } from '@/lib/format';
import { getChildren, postChildrenLinkRequest } from '@/api/parent';
import { Banner, type ChildCard, Empty, ErrorBox, Loading, PageTitle, errMsg, field, unwrap } from '../ui';

export function ChildrenPage() {
  const qc = useQueryClient();
  const [adm, setAdm] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const q = useQuery({ queryKey: ['parent-children'], queryFn: () => unwrap<ChildCard[]>(getChildren()) });
  const link = useMutation({
    mutationFn: () => postChildrenLinkRequest({ student_admission_no: adm.trim() }),
    onSuccess: (r) => { setMsg({ tone: 'success', text: (r as { data: { message?: string } }).data.message ?? 'Request sent.' }); setAdm(''); qc.invalidateQueries({ queryKey: ['parent-home'] }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  return (
    <div className="space-y-5">
      <PageTitle kicker="My children" title="Children" sub="Everything about each child: class, fees, attendance and learning." />
      {msg && <Banner tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      <Card title="My children">
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.length ? <Empty text="No children linked yet." /> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {q.data.map((c) => (
              <div key={c.student_id} className="border border-slate-200 rounded-2xl p-4 min-w-0">
                <div className="flex justify-between gap-2"><Link to={`/parent/children/${c.student_id}`} className="font-semibold truncate">{c.name}</Link><StatusBadge status={c.status} /></div>
                <div className="text-sm text-slate-500">{c.class ?? 'No class'} · {c.admission_no}{c.relationship ? ` · ${c.relationship}` : ''}</div>
                <div className="text-sm mt-2">{c.owing > 0 ? <span className="text-red-600">{naira(c.owing)} owing</span> : 'No outstanding fees'}{c.results_held && ' · results held'}</div>
                <div className="flex gap-3 mt-3 text-sm font-semibold text-brand-600">
                  <Link to={`/parent/children/${c.student_id}`}>Details</Link>
                  <Link to={`/parent/results?student=${c.student_id}`}>Results</Link>
                  <Link to={`/parent/fees?student=${c.student_id}`}>Fees</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="Link a child">
        <p className="text-sm text-slate-500 mb-3">Enter the child's admission number (it is on the school ID card and report sheet). The school confirms the request before the child appears here.</p>
        <div className="flex flex-col sm:flex-row gap-2 max-w-md">
          <input className={field} placeholder="Admission number" value={adm} onChange={(e) => setAdm(e.target.value)} />
          <Button disabled={!adm.trim() || link.isPending} onClick={() => link.mutate()}>{link.isPending ? 'Sending…' : 'Send request'}</Button>
        </div>
      </Card>
    </div>
  );
}
